// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import type { CoverageTone } from '../../types/sopGovernance';
import { MissingValue } from '../common/MissingValue';

/**
 * A coverage bar. `withLabel` appends the percentage in the same tone (the
 * per-site rows); the headline card draws its own larger figure above the bar.
 *
 * A null `pct` is unmeasured, not zero: the track still draws — the row keeps the
 * shape the design gave it — but with no fill and an em dash where the figure
 * goes, so an unpublished binding never reads as 0% coverage.
 */
export default function CoverageBar({
  pct,
  tone,
  tall,
  withLabel,
  missingReason,
}: {
  pct: number | null;
  tone: CoverageTone;
  tall?: boolean;
  withLabel?: boolean;
  /** Why `pct` is null. Required in practice whenever `pct` is null. */
  missingReason?: string;
}) {
  const bar = (
    <div className={`sg-bar${tall ? ' sg-bar--tall' : ''} sg-tone-${tone}`}>
      <div className="sg-bar-fill" style={{ width: `${pct ?? 0}%` }} />
    </div>
  );

  if (!withLabel) return bar;

  return (
    <div className="sg-bar-row">
      {bar}
      <span className={`sg-bar-pct sg-tone-${pct === null ? 'neutral' : tone}`}>
        {pct === null ? <MissingValue reason={missingReason ?? 'not published yet'} /> : `${pct}%`}
      </span>
    </div>
  );
}
